/**
 * PoreReleaseGeometryPage.tsx — Pore-Axis, Initial Velocity Vector & Plume Centre-Line Angle
 *
 * Live-bound to the active session's diffuser/module selection
 * (KEYS.DIFFUSER_MODULE). Recomputes whenever the diffuser spec changes.
 *
 * Outputs:
 *   - Pore-axis angle (disc: vertical; tube: radial distribution; dome: cant)
 *   - Initial bubble velocity vector (magnitude + direction)
 *   - Transition length where buoyancy reasserts vertical centre-line
 *   - Plume centre-line tilt under typical Barcombe wind
 *   - Adjacent module merging risk for the current array spacing
 *
 * Note: the specific correlations and the Reynolds & Bauhm release-geometry
 * scoring rules are not surfaced in the UI text — only the numerical outputs
 * and the qualitative pass/check flags. Methodology kept internally.
 */

import { useMemo, useState, useEffect } from 'react';
import { Wind, ArrowLeft, RefreshCcw, CheckCircle2, AlertTriangle, Info, Compass } from 'lucide-react';
import { useNavigate } from 'react-router';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec, type DiffuserSpec,
  poresPerUnit, poresPerModule, qPerModuleNm3h,
  bubbleDiameterMm, bubbleRiseVelMs, rhoW, sigmaW,
  describeShape, PLUME_SHAPE_LABEL,
} from '@/lib/diffuserPhysics';

const G = 9.81;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8,
  diffuserType: 'membrane_disc_epdm',
  diffuserDiamMm: 300,
  poreSizeUm: 80,
  airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0,
  mountingHeightM: 0.40,
  waterTempC: 12,
};

function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

interface ReleaseResult {
  dSpec: DiffuserSpec;
  // Pore-axis geometry
  poreAxisDescription: string;
  primaryPoreAxisDeg: number;        // 0° = vertical up
  axisSpreadDeg: number;             // half-spread of pore-axis distribution
  // Initial velocity vector
  poresEach: number;
  poresMod: number;
  q_pore_m3s: number;
  d_b_mm: number;
  v_init_pore_ms: number;
  initialMomentumN: number;
  // Trajectory
  transitionLength_m: number;        // where buoyancy reasserts vertical
  transitionShare_pct: number;       // % of total rise spent off-axis
  // Plume centre-line angle
  windSpeed_ms: number;
  plumeTiltDeg: number;
  centrelineSurfaceShift_m: number;
  // Adjacent merging
  moduleSpacing_m: number;
  mergingRisk: 'low' | 'moderate' | 'high';
  mergingNote: string;
  // Coalescence implication
  coalescenceFlag: 'ok' | 'watch' | 'high';
}

function computeReleaseGeometry(spec: ModSpec, windSpeed_ms: number, moduleSpacing_m: number): ReleaseResult {
  const dSpec = specFromLegacy(spec);
  const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
  const T = spec.waterTempC;

  // Pore axis: disc → vertical; tube → radial distribution (0–360°); dome → 10-20° cant
  let poreAxisDescription = '';
  let primaryPoreAxisDeg = 0;
  let axisSpreadDeg = 0;
  if (dSpec.shape === 'tube') {
    poreAxisDescription = 'Radial distribution around the tube circumference — bubbles released in all directions normal to the tube axis. Side-released bubbles curve upward within the first 0.1–0.3 m and rejoin the central plume.';
    primaryPoreAxisDeg = 0; // effective axis after radial averaging is still vertical
    axisSpreadDeg = 90;     // full radial spread
  } else if (spec.diffuserType === 'ceramic_dome') {
    poreAxisDescription = 'Dome surface — pore axes spread over a hemisphere. Outer pores release at 10–20° from vertical, slightly broadening the early plume footprint.';
    primaryPoreAxisDeg = 0;
    axisSpreadDeg = 18;
  } else {
    poreAxisDescription = 'Flat membrane — every pore axis is essentially vertical. Initial bubble velocity vector is straight up; predictable plume centre-line geometry.';
    primaryPoreAxisDeg = 0;
    axisSpreadDeg = 3;
  }

  // Initial velocity vector at the pore
  const poresEach = poresPerUnit(dSpec);
  const poresMod = poresPerModule(dSpec);
  const qMod = qPerModuleNm3h(dSpec);                                  // Nm³/h per module
  const q_pore_m3s = Math.max(1e-12, (qMod / 3600) / Math.max(1, poresMod));
  const d_b_m = bubbleDiameterMm(dSpec, T) / 1000;
  const d_b_mm = d_b_m * 1000;
  const A_pore_eq = Math.PI * Math.pow(spec.poreSizeUm / 2e6, 2);
  const v_init_pore_ms = q_pore_m3s / Math.max(1e-12, A_pore_eq);
  // Initial momentum of one bubble (mass of bubble × initial velocity)
  const m_bubble_kg = (Math.PI / 6) * Math.pow(d_b_m, 3) * 1.2;        // ρ_air ≈ 1.2 kg/m³
  const initialMomentumN = m_bubble_kg * v_init_pore_ms;

  // Transition length — where buoyancy overcomes initial inertia
  // Order-of-magnitude: L_trans ≈ v_init² / (2 × g × (ρ_w/ρ_g - 1))  → roughly v² / (2g × 800)
  const rho_w = rhoW(T);
  const rho_g = 1.225;
  const buoyancy_accel = G * (rho_w / rho_g - 1);                      // ~ 8000 m/s²
  const transitionLength_m = Math.min(0.8, (v_init_pore_ms * v_init_pore_ms) / (2 * Math.max(1, buoyancy_accel)) * 100);
  const transitionShare_pct = Math.min(60, 100 * transitionLength_m / H_eff);

  // Plume centre-line tilt under wind
  // Empirical envelope: tilt ≈ atan(C_wind × U10 / v_rise_plume), with C_wind ≈ 0.15
  const v_rise_plume = Math.max(0.1, bubbleRiseVelMs(d_b_mm, T) * 1.4); // plume centreline ~ 1.4× bubble rise
  const C_wind = 0.15;
  const plumeTiltDeg = Math.atan2(C_wind * windSpeed_ms, v_rise_plume) * 180 / Math.PI;
  const centrelineSurfaceShift_m = Math.tan(plumeTiltDeg * Math.PI / 180) * H_eff;

  // Adjacent module merging
  // Heuristic: merging risk = high when (2 × surface footprint radius) > 0.8 × spacing
  const surf_radius = 0.1 * H_eff + (dSpec.shape === 'tube' && dSpec.tube ? dSpec.tube.tubeDiamMm / 2000 : (dSpec.disc?.diamMm ?? 300) / 2000);
  const mergingRatio = (2 * surf_radius) / Math.max(0.1, moduleSpacing_m);
  let mergingRisk: 'low' | 'moderate' | 'high' = 'low';
  let mergingNote = '';
  if (mergingRatio > 0.95) { mergingRisk = 'high'; mergingNote = 'Adjacent module plumes will merge before reaching the surface — reduced effective oxygen transfer per module.'; }
  else if (mergingRatio > 0.7) { mergingRisk = 'moderate'; mergingNote = 'Plumes approach each other at the surface — some merging likely under wind drift.'; }
  else { mergingRisk = 'low'; mergingNote = 'Adjacent plumes remain independent across the working range of wind conditions.'; }

  // Coalescence flag — disc/tube with high pore density and high q_pore
  const surfaceTension = sigmaW(T);
  const We_orifice = rho_w * Math.pow(q_pore_m3s, 2) / (surfaceTension * Math.pow(spec.poreSizeUm / 1e6, 3));
  let coalescenceFlag: 'ok' | 'watch' | 'high' = 'ok';
  if (We_orifice > 5) coalescenceFlag = 'high';
  else if (We_orifice > 1) coalescenceFlag = 'watch';

  return {
    dSpec,
    poreAxisDescription,
    primaryPoreAxisDeg,
    axisSpreadDeg,
    poresEach,
    poresMod,
    q_pore_m3s,
    d_b_mm,
    v_init_pore_ms,
    initialMomentumN,
    transitionLength_m,
    transitionShare_pct,
    windSpeed_ms,
    plumeTiltDeg,
    centrelineSurfaceShift_m,
    moduleSpacing_m,
    mergingRisk,
    mergingNote,
    coalescenceFlag,
  };
}

export default function PoreReleaseGeometryPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
  const [windSpeed, setWindSpeed] = useState(3.0);     // m/s — typical Barcombe
  const [moduleSpacing, setModuleSpacing] = useState(12); // m — typical reservoir module spacing
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const unsub = onPersistChange((key) => {
      if (key === KEYS.DIFFUSER_MODULE || key === KEYS.CUSTOM_DIFFUSER || key === KEYS.CUSTOM_DIFF_EXPORT) {
        setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
        setNonce(n => n + 1);
      }
    });
    return () => unsub();
  }, []);

  const result = useMemo(
    () => computeReleaseGeometry(spec, windSpeed, moduleSpacing),
    [spec, windSpeed, moduleSpacing, nonce]
  );

  const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);

  // SVG vector visual
  const svgH = 280;
  const svgW = 360;
  const cx = 180;
  const bedY = svgH - 30;
  const surfaceY = 30;
  const tiltRad = result.plumeTiltDeg * Math.PI / 180;
  const surfaceCX = cx + Math.tan(tiltRad) * (bedY - surfaceY);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Wind}
        title="Pore Release Geometry & Plume Centre-Line"
        subtitle="Pore-axis distribution, initial bubble velocity vector and plume centre-line behaviour for the active diffuser selection"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Active session banner */}
      <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
        <div className="flex items-start gap-3">
          <Compass className="w-5 h-5 text-rb-brand mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100">Active diffuser selection</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {describeShape(result.dSpec)} · {PLUME_SHAPE_LABEL[result.dSpec.shape]}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Effective submergence {fmt(H_eff, 2)} m · Pores per diffuser {result.poresEach.toLocaleString()} · Pores per module {result.poresMod.toLocaleString()}
            </div>
          </div>
          <button
            onClick={() => { setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS)); setNonce(n => n + 1); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5" /> Reload session
          </button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Pore-axis spread</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">±{fmt(result.axisSpreadDeg, 0)}°</div>
          <div className="text-xs text-slate-500 mt-1">primary axis {result.primaryPoreAxisDeg.toFixed(0)}° (vertical)</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Initial velocity vector</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.v_init_pore_ms, 2)} m/s</div>
          <div className="text-xs text-slate-500 mt-1">momentum {result.initialMomentumN.toExponential(2)} N per bubble</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Transition length</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.transitionLength_m, 3)} m</div>
          <div className="text-xs text-slate-500 mt-1">{fmt(result.transitionShare_pct, 1)}% of rise before buoyancy dominates</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Plume tilt @ {windSpeed} m/s wind</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.plumeTiltDeg, 1)}°</div>
          <div className="text-xs text-slate-500 mt-1">surface shift {fmt(result.centrelineSurfaceShift_m, 2)} m</div>
        </div>
      </div>

      {/* Pore-axis description */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Pore-axis geometry</div>
        <div className="text-xs text-slate-400">{result.poreAxisDescription}</div>
      </div>

      {/* Visual + controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Centre-line under wind</div>
          <div className="text-xs text-slate-400 mb-3">
            Schematic of the plume centre-line tilt at the selected wind speed.
            Vertical dashed line is the no-wind centre-line; solid line is the wind-deflected centre-line.
          </div>
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded-lg">
            {/* surface */}
            <line x1={20} y1={surfaceY} x2={svgW - 20} y2={surfaceY} stroke="#38bdf8" strokeWidth={2} />
            <text x={25} y={surfaceY - 5} fontSize={10} fill="#38bdf8">water surface</text>
            {/* bed */}
            <line x1={20} y1={bedY} x2={svgW - 20} y2={bedY} stroke="#8c7048" strokeWidth={3} />
            <text x={25} y={bedY + 15} fontSize={10} fill="#8c7048">reservoir bed</text>
            {/* diffuser */}
            <rect x={cx - 20} y={bedY - 6} width={40} height={6} fill="#475569" />
            <text x={cx - 20} y={bedY + 22} fontSize={9} fill="#94a3b8">module</text>
            {/* no-wind centre-line */}
            <line x1={cx} y1={bedY} x2={cx} y2={surfaceY} stroke="#64748b" strokeDasharray="4 3" strokeWidth={1} />
            {/* wind-tilted centre-line */}
            <line x1={cx} y1={bedY} x2={surfaceCX} y2={surfaceY} stroke="#22c55e" strokeWidth={2} />
            <circle cx={surfaceCX} cy={surfaceY} r={4} fill="#22c55e" />
            <text x={surfaceCX + 8} y={surfaceY + 4} fontSize={10} fill="#22c55e">plume surface boil</text>
            {/* initial velocity vector */}
            <line x1={cx} y1={bedY - 6} x2={cx} y2={bedY - 28} stroke="#f97316" strokeWidth={2} markerEnd="url(#arr)" />
            <text x={cx + 8} y={bedY - 15} fontSize={9} fill="#f97316">v_init</text>
            {/* wind arrow */}
            <line x1={30} y1={50} x2={70} y2={50} stroke="#a78bfa" strokeWidth={2} markerEnd="url(#arr)" />
            <text x={75} y={54} fontSize={10} fill="#a78bfa">wind {windSpeed} m/s</text>
            {/* arrowhead marker */}
            <defs>
              <marker id="arr" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={6} markerHeight={6} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
              </marker>
            </defs>
          </svg>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-3">Inputs</div>

          <div className="text-xs text-slate-400 mb-1">Wind speed (10 m above water, m/s)</div>
          <div className="flex items-center gap-3 mb-4">
            <input type="range" min={0} max={12} step={0.5} value={windSpeed}
              onChange={e => setWindSpeed(Number(e.target.value))} className="flex-1" />
            <div className="w-12 text-right text-slate-100 font-semibold">{windSpeed.toFixed(1)}</div>
          </div>

          <div className="text-xs text-slate-400 mb-1">Module-to-module spacing (m)</div>
          <div className="flex items-center gap-3 mb-4">
            <input type="range" min={3} max={30} step={0.5} value={moduleSpacing}
              onChange={e => setModuleSpacing(Number(e.target.value))} className="flex-1" />
            <div className="w-12 text-right text-slate-100 font-semibold">{moduleSpacing.toFixed(1)}</div>
          </div>

          <div className={`rounded-lg border p-3 ${
            result.mergingRisk === 'low' ? 'border-emerald-700 bg-emerald-900/20' :
            result.mergingRisk === 'moderate' ? 'border-amber-700 bg-amber-900/20' :
            'border-red-700 bg-red-900/20'
          }`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-100 mb-1">
              {result.mergingRisk === 'low'
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                : <AlertTriangle className="w-4 h-4 text-amber-400" />}
              Adjacent plume merging: {result.mergingRisk.toUpperCase()}
            </div>
            <div className="text-xs text-slate-400">{result.mergingNote}</div>
          </div>
        </div>
      </div>

      {/* Detailed numeric outputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Numeric outputs</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {[
            ['Bubble Ø at release', `${fmt(result.d_b_mm, 2)} mm`],
            ['Effective submergence', `${fmt(H_eff, 2)} m`],
            ['Pores per diffuser', result.poresEach.toLocaleString()],
            ['Pores per module', result.poresMod.toLocaleString()],
            ['Air flow per pore', `${(result.q_pore_m3s * 1e6).toFixed(2)} mL/s`],
            ['Initial pore-exit velocity', `${fmt(result.v_init_pore_ms, 3)} m/s`],
            ['Initial bubble momentum', `${result.initialMomentumN.toExponential(2)} N`],
            ['Transition length to buoyant centre-line', `${fmt(result.transitionLength_m, 3)} m`],
            ['Share of rise spent off-axis', `${fmt(result.transitionShare_pct, 1)} %`],
            ['Wind speed', `${windSpeed.toFixed(1)} m/s`],
            ['Plume centre-line tilt', `${fmt(result.plumeTiltDeg, 1)} °`],
            ['Centre-line shift at surface', `${fmt(result.centrelineSurfaceShift_m, 2)} m`],
            ['Module-to-module spacing', `${moduleSpacing.toFixed(1)} m`],
            ['Coalescence regime flag', result.coalescenceFlag.toUpperCase()],
          ].map(([k, v], i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-slate-800 pb-1.5">
              <span className="text-slate-400">{k}</span>
              <span className="text-slate-100 font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Why this matters */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Why pore-axis and centre-line angle matter
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Bubbles always end up rising vertically once buoyancy dominates over initial inertia.
            The release geometry of the diffuser therefore matters most in the first 0.1–0.5 m of rise —
            but that is also where pore-to-pore coalescence happens, where adjacent-module plume merging
            is set, and where wind-drift starts to deflect the plume centre-line.
          </p>
          <p>
            A flat-disc release gives the most predictable plume centre-line and the easiest layout to
            optimise. A radial (tube) release broadens the early footprint but the plume re-organises
            vertically within the transition length above. A domed release sits between the two.
            None of these is "best" in the abstract — the right answer for Barcombe depends on bed slope,
            module spacing and prevailing wind, all of which are tracked through the active session.
          </p>
        </div>
      </div>

      {/* Methodology disclosure */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Methodology note</div>
        <div className="text-xs text-slate-400 leading-relaxed">
          The specific correlations used for transition length, plume tilt under wind and adjacent-plume
          merging risk are part of the in-house Reynolds &amp; Bauhm Smart Water design framework.
          Only the resulting numerical outputs, qualitative flags and the schematic visualisation are
          surfaced on this page. The full method is documented internally and would be presented to
          the client under appropriate commercial cover.
        </div>
      </div>
    </div>
  );
}
